import React from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon';

/**
 * Toast — 일시적 알림 컴포넌트
 *
 * 화면 우측 상단에 표시되는 알림. 유형별 아이콘과 색상을 적용하며,
 * slide-in / fade-out 애니메이션을 지원한다.
 *
 * @param {Object} props
 * @param {string} props.id - 고유 식별자
 * @param {'success'|'error'|'warning'|'info'} props.type - 알림 유형
 * @param {string} props.message - 알림 메시지
 * @param {Function} props.onClose - 닫기 콜백
 * @returns {React.ReactElement}
 *
 * 사용 예:
 *   <Toast id="toast-1" type="success" message="저장되었습니다" onClose={handleClose} />
 *   <Toast id="toast-2" type="error" message="실패했습니다" onClose={handleClose} />
 */

const TOAST_CONFIG = {
  success: { icon: 'check_circle', colorVar: '--color-success' },
  error: { icon: 'error', colorVar: '--color-error' },
  warning: { icon: 'warning', colorVar: '--color-warning' },
  info: { icon: 'info', colorVar: '--color-info' },
};

const Toast = ({ id, type = 'info', message, onClose }) => {
  const config = TOAST_CONFIG[type] || TOAST_CONFIG.info;

  return (
    <div
      className={`toast toast--${type}`}
      role="alert"
      data-toast-id={id}
    >
      <Icon
        name={config.icon}
        size={20}
        color={`var(${config.colorVar})`}
        className="toast__icon"
      />
      <span className="toast__message">{message}</span>
      <button
        className="toast__close"
        onClick={onClose}
        aria-label="알림 닫기"
        type="button"
      >
        <Icon name="close" size={16} color="var(--color-text-secondary)" />
      </button>
    </div>
  );
};

Toast.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export { TOAST_CONFIG };
export default Toast;
